import { Transition } from "@headlessui/react";
import { buildChatBotInstallationSnippets } from "@jugl-web/domain-resources/chat-bot/utils/buildChatBotInstallationSnippets";
import { cx, useClipboard, useTranslations } from "@jugl-web/utils";
import { useLocalStorage } from "@jugl-web/utils/hooks/useStorage";
import { SHOW_CHAT_BOT_LOADER_SETTINGS_UPDATED_ALERT_KEY } from "@jugl-web/utils/storage";
import { environment } from "@web-src/environments/environment";
import { useWebChatBotAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/WebChatBotAccountProvider/useWebChatBotAccount";
import { CXRobotAlert } from "@web-src/modules/cx/components/CXRobotAlert";
import { FC, useMemo } from "react";
import { PlainButton } from "@jugl-web/ui-components/cross-platform/PlainButton";
import { ReactComponent as CrossIcon } from "./assets/cross.svg";

interface RepasteSnippetCodePopupProps {
  isOpen: boolean;
  onClose: () => void;
  onAfterClose?: () => void;
}

const BOT_JS_URL = environment.chatBotUrl;

export const RepasteSnippetCodeAlert: FC<RepasteSnippetCodePopupProps> = ({
  isOpen,
  onClose,
  onAfterClose,
}) => {
  const { webChatBotAccount } = useWebChatBotAccount();

  const [, setWasLoaderSettingsUpdated] = useLocalStorage(
    SHOW_CHAT_BOT_LOADER_SETTINGS_UPDATED_ALERT_KEY,
    false
  );

  const { webScript } = useMemo(
    () =>
      buildChatBotInstallationSnippets({
        token: webChatBotAccount?.token ?? "",
        rawSettings: webChatBotAccount?.settings ?? {},
        chatbotUrl: BOT_JS_URL,
      }),
    [webChatBotAccount]
  );

  const { copyToClipboard } = useClipboard();
  const { t } = useTranslations();

  const handleCopy = () => {
    copyToClipboard(webScript);
    setWasLoaderSettingsUpdated(false);
    onClose();
  };

  return (
    <Transition
      as="div"
      afterLeave={onAfterClose}
      show={isOpen}
      enter="transition"
      enterFrom="opacity-0 -translate-x-4 scale-75"
      enterTo="opacity-100 translate-x-0 scale-100"
      leave="transition"
      leaveFrom="opacity-100 translate-x-0 scale-100"
      leaveTo="opacity-0 -translate-x-4 scale-75"
      className={cx(
        "util-prevent-drawer-close group fixed right-8 bottom-8 z-[200] origin-bottom-right duration-300",
        "w-[360px] rounded-[20px]"
      )}
    >
      <PlainButton
        className="absolute -top-2 -right-2 flex h-8 w-8 items-center justify-center rounded-full bg-white opacity-0 shadow-[0_13px_56px_rgba(0,0,0,0.16)] transition hover:scale-110 active:scale-90 group-hover:opacity-100"
        onClick={onClose}
      >
        <CrossIcon />
      </PlainButton>
      <CXRobotAlert
        title={t({
          id: "cx-ai-agents-page.repaste-snippet-code-alert-title",
          defaultMessage: "Apply changes to your website",
        })}
        text={t({
          id: "cx-ai-agents-page.repaste-snippet-code-alert-text",
          defaultMessage:
            "Copy and paste the new code snippet into your website to see the updated chat appearance.",
        })}
        buttonLabel={t({
          id: "cx-ai-agents-page.repaste-snippet-code-alert-button-label",
          defaultMessage: "Copy code",
        })}
        className="rounded-[20px]"
        onClick={handleCopy}
      />
    </Transition>
  );
};
